import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaFilter, FaRupeeSign } from "react-icons/fa";
import { FiX } from "react-icons/fi";
import Select from "react-select";

import { useGetDebtCreditReportQuery } from "../toolkitServices/cashFlowApi";
import { useGetAllBranchesQuery } from "../toolkitServices/branchApi";
import { useGetAllCustomersQuery } from "../toolkitServices/customerApi";

const fmt2 = (n) => Number(n || 0).toFixed(2);
const money = (n) => `₹ ${fmt2(n)}`;
const dIN = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("en-IN");
};

const THEME = {
  ring: "#7c3aed",
};

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    borderRadius: 12,
    borderColor: state.isFocused ? THEME.ring : "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 3px #7c3aed22" : "none",
    "&:hover": { borderColor: THEME.ring },
  }),
  menu: (base) => ({
    ...base,
    zIndex: 70,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
};

function SummaryStrip({ totalInvoices, totalOutstanding }) {
  return (
    <div className="shrink-0 border-b bg-gradient-to-r from-slate-50 via-violet-50/60 to-slate-50 px-2.5 py-2 sm:px-4 sm:py-2.5">
      <div className="grid grid-cols-2 gap-2 sm:gap-3">
        <SummaryCard label="Total Invoices" value={totalInvoices} />
        <SummaryCard
          label="Total Outstanding"
          value={money(totalOutstanding)}
          outstanding
        />
      </div>
    </div>
  );
}

function TableSkeletonRows() {
  return Array.from({ length: 8 }).map((_, i) => (
    <tr key={`sk-${i}`} className="border-t">
      <td className="px-3 py-3">
        <div className="h-4 w-24 bg-slate-200 rounded animate-pulse" />
      </td>
      <td className="px-3 py-3">
        <div className="h-4 w-20 bg-slate-200 rounded animate-pulse" />
      </td>
      <td className="hidden sm:table-cell px-3 py-3">
        <div className="h-4 w-28 bg-slate-200 rounded animate-pulse" />
      </td>
      <td className="hidden lg:table-cell px-3 py-3">
        <div className="h-4 w-24 bg-slate-200 rounded animate-pulse" />
      </td>
      <td className="px-3 py-3 text-right">
        <div className="h-4 w-20 bg-slate-200 rounded animate-pulse ml-auto" />
      </td>
      <td className="hidden md:table-cell px-3 py-3 text-right">
        <div className="h-4 w-20 bg-slate-200 rounded animate-pulse ml-auto" />
      </td>
      <td className="px-3 py-3 text-right">
        <div className="h-4 w-20 bg-slate-200 rounded animate-pulse ml-auto" />
      </td>
    </tr>
  ));
}

export default function DebtCreditReport() {
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const [branchId, setBranchId] = useState("");
  const [customerId, setCustomerId] = useState("");

  const [draftBranchId, setDraftBranchId] = useState("");
  const [draftCustomerId, setDraftCustomerId] = useState("");

  const { data, isFetching, isError, error } = useGetDebtCreditReportQuery({
    branchId,
    customerId,
  });

  const { data: branches } = useGetAllBranchesQuery();
  const { data: customers } = useGetAllCustomersQuery();

  const branchOptions = useMemo(
    () =>
      branches?.data?.map((b) => ({
        label: b.branchName,
        value: b._id,
      })) || [],
    [branches]
  );

  const customerOptions = useMemo(
    () =>
      customers?.data?.map((c) => ({
        label: c.customerName || c.customerId || "Customer",
        value: c._id,
      })) || [],
    [customers]
  );

  const entries = data?.entries || [];
  const totalOutstanding = data?.totalOutstanding || 0;
  const totalInvoices = data?.totalInvoices || 0;

  const openFilterModal = () => {
    setDraftBranchId(branchId);
    setDraftCustomerId(customerId);
    setIsFilterOpen(true);
  };

  const clearAppliedFilters = () => {
    setBranchId("");
    setCustomerId("");
  };

  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setIsFilterOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, []);

  return (
    <div className="flex h-[calc(100vh-140px)] flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg md:h-[calc(100vh-70px)]">
      {/* Header */}
      <div className="flex shrink-0 items-center justify-between gap-3 bg-gradient-to-r from-[#6b59c6] via-[#806dcc] to-[#9481ce] px-3 py-2.5 text-white shadow-sm sm:px-4">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/20">
            <FaRupeeSign className="text-sm" />
          </span>
          <div className="min-w-0">
            <h2 className="truncate text-sm font-semibold tracking-tight sm:text-lg">
              Outstanding Report
            </h2>
            <p className="truncate text-[9px] text-violet-100 sm:text-[11px]">
              Pending invoice balances
            </p>
          </div>
          {isFetching && (
            <span className="hidden items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[10px] sm:inline-flex">
              <span className="h-2.5 w-2.5 animate-spin rounded-full border border-white border-t-transparent" />
              refreshing…
            </span>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <span className="hidden rounded-full bg-white/15 px-2.5 py-1 text-[10px] font-medium ring-1 ring-white/10 sm:inline-flex">
            {entries.length} invoices
          </span>
          <button
            type="button"
            onClick={openFilterModal}
            className="inline-flex h-9 w-9 items-center justify-center gap-1.5 rounded-xl bg-white/15 text-white ring-1 ring-white/20 transition hover:bg-white/25 sm:w-auto sm:rounded-full sm:px-3"
            title="Filters"
            aria-label="Open filters"
          >
            <FaFilter className="text-xs" />
            <span className="hidden text-[11px] font-semibold sm:inline">
              Filters
            </span>
          </button>
        </div>
      </div>

      {/* Active filters */}
      {(branchId || customerId) && (
        <div className="flex shrink-0 items-center gap-2 border-b bg-slate-50 px-3 py-1.5 sm:px-4">
          <div className="flex min-w-0 flex-1 gap-1.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {branchId && (
              <FilterChip
                label={`Branch: ${
                  branchOptions.find((branch) => branch.value === branchId)
                    ?.label || "Selected"
                }`}
              />
            )}
            {customerId && (
              <FilterChip
                label={`Customer: ${
                  customerOptions.find(
                    (customer) => customer.value === customerId,
                  )?.label || "Selected"
                }`}
              />
            )}
          </div>
          <button
            type="button"
            onClick={clearAppliedFilters}
            className="shrink-0 text-[10px] font-semibold text-[#6b59c6] hover:underline"
          >
            Clear all
          </button>
        </div>
      )}

      {/* SUMMARY */}
      {!isFetching && entries.length > 0 && (
        <div className="shrink-0">
          <SummaryStrip
            totalInvoices={totalInvoices}
            totalOutstanding={totalOutstanding}
          />
        </div>
      )}

      {/* Invoices */}
      <div className="min-h-0 flex-1 overflow-auto bg-slate-50/70 p-2.5 sm:p-4">
        <div className="h-full overflow-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full text-xs sm:text-sm">
            <thead className="sticky top-0 z-10 bg-[#efeafd] text-[10px] uppercase tracking-wide text-[#4b3d91] sm:text-[11px]">
              <tr>
                <th className="px-3 py-2.5 text-left">Invoice</th>
                <th className="px-3 py-2.5 text-left">Date</th>
                <th className="hidden px-3 py-2.5 text-left sm:table-cell">
                  Customer
                </th>
                <th className="hidden px-3 py-2.5 text-left lg:table-cell">
                  Branch
                </th>
                <th className="px-3 py-2.5 text-right">Grand Total</th>
                <th className="hidden bg-emerald-50/60 px-3 py-2.5 text-right text-emerald-700 md:table-cell">
                  Paid
                </th>
                <th className="border-l-2 border-l-rose-200 bg-rose-50/70 px-3 py-2.5 text-right text-rose-700">
                  Outstanding
                </th>
              </tr>
            </thead>

            <tbody className="bg-white text-slate-700">
              {isFetching && <TableSkeletonRows />}

              {!isFetching && isError && (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-rose-600">
                    {error?.data?.message ||
                      "Unable to load outstanding invoices"}
                  </td>
                </tr>
              )}

              {!isFetching && !isError && entries.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-slate-500">
                    No outstanding invoices found
                  </td>
                </tr>
              )}

              {!isFetching && !isError &&
                entries.map((inv) => (
                  <tr
                    key={inv._id}
                    className="border-t border-slate-100 transition hover:bg-violet-50/40"
                  >
                    <td className="px-3 py-3">
                      <div className="font-semibold text-slate-900 truncate max-w-[120px] sm:max-w-none">
                        {inv.invCode}
                      </div>
                      <div className="sm:hidden text-[10px] text-slate-500 mt-0.5">
                        {inv.branchId?.branchName || "-"}
                      </div>
                    </td>

                    <td className="px-3 py-3 whitespace-nowrap">
                      {dIN(inv.invDate)}
                    </td>

                    <td className="hidden sm:table-cell px-3 py-3">
                      <div className="max-w-[220px] truncate">
                        {inv.customerId?.customerId
                          ? `${inv.customerId.customerId} - `
                          : ""}
                        {inv.customerId?.customerName || "-"}
                      </div>
                    </td>

                    <td className="hidden lg:table-cell px-3 py-3">
                      {inv.branchId?.branchName || "-"}
                    </td>

                    <td className="px-3 py-3 text-right whitespace-nowrap">
                      {money(inv.totals?.grandTotal)}
                    </td>

                    <td className="hidden whitespace-nowrap bg-emerald-50/30 px-3 py-3 text-right font-medium tabular-nums text-emerald-700 md:table-cell">
                      {money(inv.totals?.paid)}
                    </td>

                    <td className="whitespace-nowrap border-l-2 border-l-rose-100 bg-rose-50/30 px-3 py-3 text-right font-bold tabular-nums text-rose-700">
                      {money(inv.totals?.outstanding)}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Filter panel */}
      <AnimatePresence>
        {isFilterOpen && (
          <motion.div
            className="fixed inset-0 z-[9998] flex items-end justify-center bg-slate-950/40 p-3 backdrop-blur-[1px] sm:items-center sm:p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsFilterOpen(false)}
          >
            <motion.div
              className="w-full max-w-[520px] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
              initial={{ y: 30, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 30, opacity: 0, scale: 0.98 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between bg-gradient-to-r from-[#6b59c6] to-[#9481ce] px-4 py-3 text-white">
                <div>
                  <h3 className="text-sm font-semibold">Outstanding Filters</h3>
                  <p className="mt-0.5 text-[10px] text-violet-100">
                    Filter by branch and customer
                  </p>
                </div>
                <button
                  type="button"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/15 transition hover:bg-white/25"
                  onClick={() => setIsFilterOpen(false)}
                  aria-label="Close filters"
                >
                  <FiX />
                </button>
              </div>

              <div className="space-y-3 p-4">
                <div>
                  <label className="mb-1 block text-[10px] font-medium text-slate-600">
                    Branch
                  </label>
                  <Select
                    styles={selectStyles}
                    options={branchOptions}
                    menuPortalTarget={document.body}
                    value={
                      draftBranchId
                        ? branchOptions.find((x) => x.value === draftBranchId) || null
                        : null
                    }
                    onChange={(o) => setDraftBranchId(o?.value || "")}
                    placeholder="Select branch"
                    isClearable
                  />
                </div>

                <div>
                  <label className="mb-1 block text-[10px] font-medium text-slate-600">
                    Customer
                  </label>
                  <Select
                    styles={selectStyles}
                    options={customerOptions}
                    menuPortalTarget={document.body}
                    value={
                      draftCustomerId
                        ? customerOptions.find((x) => x.value === draftCustomerId) || null
                        : null
                    }
                    onChange={(o) => setDraftCustomerId(o?.value || "")}
                    placeholder="Select customer"
                    isClearable
                  />
                </div>
              </div>

              <div className="flex items-center justify-between gap-2 border-t bg-slate-50 px-4 py-3">
                <button
                  type="button"
                  onClick={() => {
                    setDraftBranchId("");
                    setDraftCustomerId("");
                  }}
                  className="text-[11px] font-semibold text-slate-500 hover:text-slate-700"
                >
                  Reset
                </button>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setIsFilterOpen(false)}
                    className="rounded-full border border-slate-200 bg-white px-4 py-2 text-[11px] font-semibold text-slate-600"
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setBranchId(draftBranchId);
                      setCustomerId(draftCustomerId);
                      setIsFilterOpen(false);
                    }}
                    className="rounded-full bg-[#6b59c6] px-5 py-2 text-[11px] font-semibold text-white shadow-sm transition hover:bg-[#5948ae]"
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function SummaryCard({ label, value, outstanding = false }) {
  return (
    <div
      className={`min-w-0 rounded-xl border px-3 py-2 text-center shadow-sm ${
        outstanding
          ? "border-rose-100 bg-rose-50 text-rose-700"
          : "border-violet-100 bg-white text-[#4b3d91]"
      }`}
    >
      <p className="truncate text-[9px] font-semibold uppercase tracking-wide opacity-80 sm:text-[10px]">
        {label}
      </p>
      <p className="mt-0.5 truncate text-[11px] font-bold tabular-nums sm:text-sm">
        {value}
      </p>
    </div>
  );
}

function FilterChip({ label }) {
  return (
    <span className="shrink-0 rounded-full border border-violet-100 bg-white px-2.5 py-1 text-[10px] font-medium text-slate-600">
      {label}
    </span>
  );
}
